import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Form, Spinner } from "react-bootstrap";
import Modal from "../../../../../layout/modal";
import ReportList, { BLANK_EMAIL_OPTION } from "../../../../../layout/select/report-list";
import TemplateCustomFieldInputs from "../../../../../layout/template-custom-fields/inputs";
import {
  getTemplateCustomFields,
  validateCustomValues
} from "../../../../../utils/template-custom-fields";

function ReportModal({ show, items = [], onOk, onCancel, working = false, workingLabel = "" }) {
  const normalizedItems = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const [selected, setSelected] = useState(null);
  const [custom, setCustom] = useState({});
  const [customErrors, setCustomErrors] = useState({});
  const [signatureRequest, setSignatureRequest] = useState(false);
  const selectedTemplate = selected?.target?.value;
  const customFields = useMemo(() => getTemplateCustomFields(selectedTemplate), [selectedTemplate]);
  const canPrepareSignatureRequest = ["RTF", "DXP"].includes(selectedTemplate?.output);

  // Reset/prefresh selection when modal opens or item list changes
  useEffect(() => {
    if (!show) return;

    // Keep current selection if still present
    const stillValid =
      selected?.target?.value?.blank ||
      (
        selected?.target?.value?.uuid &&
        normalizedItems.some((x) => x.uuid === selected.target.value.uuid)
      );

    if (stillValid) return;

    setSelected({
      target: {
        name: "report",
        value: BLANK_EMAIL_OPTION
      }
    });
    setCustom({});
    setCustomErrors({});
  }, [show, normalizedItems, selected]);

  useEffect(() => {
    setCustom({});
    setCustomErrors({});
    setSignatureRequest(false);
  }, [selectedTemplate?.uuid]);

  const handleOk = () => {
    if (!selected || working) return;
    const validation = validateCustomValues(customFields, custom);
    if (!validation.valid) {
      setCustomErrors(validation.errors);
      return;
    }

    onOk?.({ report: selected, custom, signatureRequest });
  };

  const handleCustomChange = (name, value) => {
    if (working) return;

    setCustom((prev) => ({
      ...prev,
      [name]: value
    }));
    setCustomErrors((prev) => ({
      ...prev,
      [name]: undefined
    }));
  };

  const handleCancel = () => {
    if (working) return;

    onCancel?.();
  };

  const handleReportChange = (value) => {
    if (working) return;

    setSelected(value);
  };

  const handleSignatureRequestChange = (event) => {
    if (working) return;

    setSignatureRequest(event.target.checked);
  };

  return (
    <Modal
      show={show}
      title="What Would You Like to Generate?"
      body="Pick the artifact you want to create."
      buttons={[
        {
          caption: working ? "Working..." : "ok",
          onEnabledClick: handleOk,
          variant: "primary",
          enabled: Boolean(selected) && !working,
          display: true
        },
        {
          default: true,
          caption: "cancel",
          onEnabledClick: handleCancel,
          variant: "primary",
          enabled: !working,
          display: true
        }
      ]}
    >
      <Row className="g-0 sh-6 mb-2 mt-3">
        <Col>
          <div className="mb-3 top-label">
            <ReportList items={normalizedItems} selected={selected} onChange={handleReportChange} isDisabled={working} />
            <Form.Label>DOCUMENT</Form.Label>
          </div>
        </Col>
      </Row>
      <TemplateCustomFieldInputs
        fields={customFields}
        values={custom}
        errors={customErrors}
        onChange={handleCustomChange}
      />
      {canPrepareSignatureRequest ? (
        <Form.Check
          type="switch"
          id="person-report-signature-request"
          label="Prepare signature request"
          checked={signatureRequest}
          disabled={working}
          onChange={handleSignatureRequestChange}
        />
      ) : null}
      {working ? (
        <div className="d-flex align-items-center text-muted mt-3">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>{workingLabel || "Generating..."}</span>
        </div>
      ) : null}
    </Modal>
  );
}

export default ReportModal;
